import { PRODUCT_DETAIL_PAGE } from '../analytics/product-list-context';
import type { EcommerceProductData } from '../analytics/types';
import { mountQuantityStepper } from '../shared/quantity-stepper';
import { scrollToConfigurator } from '../shared/scroll-to-configurator';
import { initToggletipGroup } from '../shared/toggletip';
import {
  type AvailabilityResolver,
  type AvailabilitySnapshot,
  createAvailabilityResolver
} from './swatches-pricing-options-block/availability';
import {
  type ConfiguratorConfig,
  parseConfiguratorConfig
} from './swatches-pricing-options-block/configurator-config';
import { type WizardView, createWizard } from './swatches-pricing-options-block/wizard';

const ADD_TO_CART_TIMEOUT_MS = 15000;
const SUCCESS_RESET_MS = 1600;

export interface SwatchesConfiguratorOptions {
  resolver?: AvailabilityResolver;
}

function formatPrice(value: number, currency: string): string {
  try {
    return new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency',
      currency: currency || 'USD',
      maximumFractionDigits: 0
    }).format(value);
  } catch {
    return `${currency} ${value}`;
  }
}

function render(root: HTMLElement, config: ConfiguratorConfig, view: WizardView): void {
  root.dataset.wizardStatus = view.status;

  root.querySelectorAll<HTMLElement>('[data-wizard-page]').forEach(page => {
    page.hidden = Number.parseInt(page.dataset.wizardPage ?? '', 10) !== view.pageIndex;
  });

  const allowedByAttribute = new Map(
    (view.snapshot?.groups ?? []).map(group => [group.attributeCode, group.values])
  );

  root.querySelectorAll<HTMLElement>('[data-option-group]').forEach(group => {
    const attributeCode = group.dataset.attributeCode;
    const allowed = attributeCode ? allowedByAttribute.get(attributeCode) : undefined;

    group.querySelectorAll<HTMLElement>('[data-option-row]').forEach(row => {
      const valueIndex = Number.parseInt(row.dataset.valueIndex ?? '', 10);
      const input = row.querySelector<HTMLInputElement>('.option-row__input');
      const match = allowed?.find(value => value.valueIndex === valueIndex);

      if (!input || !allowed) {
        return;
      }

      const isAvailable = match?.isAvailable ?? false;

      input.disabled = !isAvailable;
      input.checked = match?.isSelected ?? false;
      row.classList.toggle('option-row--disabled', !isAvailable);
    });
  });

  if (view.snapshot?.lowestFinalPrice !== undefined) {
    const resolved = formatPrice(view.snapshot.lowestFinalPrice, view.snapshot.currency);

    root.querySelectorAll<HTMLElement>('[data-wizard-price]').forEach(price => {
      price.textContent = resolved;
    });
  }

  const activePage = root.querySelector<HTMLElement>(`[data-wizard-page="${view.pageIndex}"]`);
  const next = activePage?.querySelector<HTMLButtonElement>('[data-wizard-next]');
  const addToCart = activePage?.querySelector<HTMLButtonElement>('[data-add-to-cart]');

  if (next) {
    next.hidden = view.isLastPage;
    next.disabled = !view.canAdvance;
  }

  if (addToCart) {
    addToCart.hidden = !view.isLastPage;
    addToCart.disabled = !view.canAddToCart;
  }
}

function buildEcommerceProduct(
  root: HTMLElement,
  snapshot: AvailabilitySnapshot,
  planName: string,
  quantity: number
): EcommerceProductData | null {
  if (!snapshot.matchedSku) {
    return null;
  }

  return {
    currency: snapshot.currency || undefined,
    item: {
      item_id: snapshot.matchedSku,
      item_name: planName,
      item_list_id: PRODUCT_DETAIL_PAGE.id,
      item_list_name: PRODUCT_DETAIL_PAGE.name,
      price: snapshot.lowestFinalPrice,
      quantity
    }
  };
}

export function mountSwatchesConfigurator(
  root: HTMLElement,
  options?: SwatchesConfiguratorOptions
): void {
  const configJson = root.querySelector<HTMLScriptElement>(
    '[data-configurator-config]'
  )?.textContent;
  const config = parseConfiguratorConfig(configJson);
  const productGuid = root.dataset.productGuid?.trim();

  if (!config || !productGuid) {
    return;
  }

  const resolver =
    options?.resolver ??
    createAvailabilityResolver(root.dataset.apiBaseUrl ?? '', productGuid, config);

  const configurator = root.querySelector<HTMLElement>('[data-pricing-configurator]');
  const configureButton = root.querySelector<HTMLButtonElement>(
    '[data-modal-trigger], [data-pricing-configure]'
  );
  const summary = root.querySelector<HTMLElement>('[data-pricing-summary]');
  const quantityRoot = root.querySelector<HTMLElement>(
    '[data-configurator-quantity] [data-quantity-stepper]'
  );
  const quantityStepper = quantityRoot ? mountQuantityStepper(quantityRoot) : null;
  const status = root.querySelector<HTMLElement>('[data-product-options-status]');

  const wizard = createWizard(config, resolver, view => render(root, config, view));

  const getActiveFeedback = (): HTMLElement | null => {
    const { pageIndex } = wizard.getView();
    return root.querySelector<HTMLElement>(
      `[data-wizard-page="${pageIndex}"] [data-product-options-feedback]`
    );
  };

  let started = false;

  const showConfigurator = (): void => {
    const isModal = Boolean(configurator?.closest('dialog'));

    if (!isModal) {
      if (summary) summary.hidden = true;
      if (configurator) configurator.hidden = false;
    }

    if (!started) {
      started = true;
      void wizard.start();
    }

    if (!isModal && configurator) {
      configurator.focus({ preventScroll: true });
      window.requestAnimationFrame(() => scrollToConfigurator(configurator));
    }
  };

  const showSummary = (): void => {
    const dialog = configurator?.closest<HTMLDialogElement>('dialog');
    if (dialog) {
      dialog.close();
      return;
    }

    if (configurator) configurator.hidden = true;
    if (summary) summary.hidden = false;
    configureButton?.focus();
  };

  configureButton?.addEventListener('click', showConfigurator);

  root.addEventListener('change', event => {
    const input = (event.target as HTMLElement)?.closest<HTMLInputElement>('.option-row__input');
    const group = input?.closest<HTMLElement>('[data-option-group]');
    const row = input?.closest<HTMLElement>('[data-option-row]');
    const attributeCode = group?.dataset.attributeCode;
    const valueIndex = Number.parseInt(row?.dataset.valueIndex ?? '', 10);

    if (!attributeCode || !Number.isFinite(valueIndex)) {
      return;
    }

    void wizard.select(attributeCode, valueIndex);
  });

  root.querySelectorAll<HTMLButtonElement>('[data-wizard-previous]').forEach(previousButton => {
    previousButton.addEventListener('click', () => {
      if (wizard.getView().isFirstPage) {
        showSummary();
        return;
      }

      wizard.previous();
    });
  });

  root.querySelectorAll<HTMLButtonElement>('[data-wizard-next]').forEach(nextButton => {
    nextButton.addEventListener('click', () => {
      wizard.next();
    });
  });

  root.querySelectorAll<HTMLButtonElement>('[data-add-to-cart]').forEach(addButton => {
    const buttonLabel = addButton.querySelector<HTMLElement>('[data-button-label]');
    const defaultAddLabel = buttonLabel?.textContent?.trim() || 'Add to cart';

    addButton.addEventListener('click', () => {
      const { snapshot } = wizard.getView();

      if (!snapshot?.matchedSku) {
        return;
      }

      const planName = root.dataset.planName?.trim() || 'item';
      const quantity = quantityStepper?.getQuantity() ?? 1;
      const requestId = `${snapshot.matchedSku}-${Date.now()}`;

      addButton.disabled = true;
      addButton.dataset.feedbackState = 'loading';
      if (buttonLabel) buttonLabel.textContent = 'Adding…';
      const feedback = getActiveFeedback();
      if (feedback) {
        feedback.hidden = true;
        feedback.textContent = '';
      }

      const resetButton = (): void => {
        addButton.removeAttribute('data-feedback-state');
        if (buttonLabel) buttonLabel.textContent = defaultAddLabel;
        render(root, config, wizard.getView());
      };

      const showError = (
        message = 'Unable to add this configuration right now. Please try again.'
      ): void => {
        resetButton();
        const feedback = getActiveFeedback();
        if (feedback) {
          feedback.hidden = false;
          feedback.dataset.feedbackState = 'error';
          feedback.textContent = message;
        }
        if (status) status.textContent = `Unable to add ${planName} to cart.`;
      };

      const onResult = (event: Event): void => {
        const result = (
          event as CustomEvent<{ requestId: string; success: boolean; error?: string }>
        ).detail;
        if (result.requestId !== requestId) return;

        window.clearTimeout(loadTimeout);
        document.removeEventListener('bentley:add-to-cart-result', onResult);

        if (!result.success) {
          showError(result.error);
          return;
        }

        addButton.dataset.feedbackState = 'success';
        if (buttonLabel) buttonLabel.textContent = 'Added to cart';
        if (status) status.textContent = `Added ${planName} to cart.`;
        window.setTimeout(resetButton, SUCCESS_RESET_MS);
      };

      document.addEventListener('bentley:add-to-cart-result', onResult);
      const loadTimeout = window.setTimeout(() => {
        document.removeEventListener('bentley:add-to-cart-result', onResult);
        showError();
      }, ADD_TO_CART_TIMEOUT_MS);

      document.dispatchEvent(
        new CustomEvent('bentley:add-to-cart-request', {
          detail: {
            requestId,
            sku: snapshot.matchedSku,
            parentSku: root.dataset.sku?.trim() || undefined,
            quantity,
            planName,
            ecommerceProduct: buildEcommerceProduct(root, snapshot, planName, quantity)
          }
        })
      );
    });
  });

  if (!configureButton) {
    started = true;
    void wizard.start();
  }
}

export default function swatchesPricingOptionsBlock(root: HTMLElement): void {
  initToggletipGroup(root, { idPrefix: 'pricing-options-note', describedBy: true });
  mountSwatchesConfigurator(root);
}
